import React, { useCallback, useState, useEffect, useContext } from 'react'
import {
  IconButton,
  DialogActions,
  Dialog,
  useMediaQuery,
  useTheme,
  Box,
  TextField,
  Button,
  Typography,
  DialogTitle,
  DialogContent,
  InputLabel
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { axiosInstanceMain } from '../../utils/axiosGlobalUtils'
import { TenantsContext } from '../../context/tenants.context'
import { useDropzone } from 'react-dropzone'
import { DeleteOutline } from '@mui/icons-material'
import SnackBarUtils from '../../utils/SnackBarUtils'
import { MAX_IMAGE_SIZE_BYTES, MAX_IMAGE_SIZE_MB } from '../Products/components/Dropzone'

interface UploadImgProps {
  open: boolean
  setOpen: (open: boolean) => void
  codLocal: string
  updateGaleria: () => void
}

const MAX_TITULO_LENGTH = 75

const UploadImg: React.FC<UploadImgProps> = ({ open, setOpen, codLocal, updateGaleria }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { tenant } = useContext(TenantsContext)
  const [loading, setLoading] = useState<boolean>(false)
  const [titulo, setTitulo] = useState<string>(null)
  const [dropError, setDropError] = useState<string>()
  const [preview, setPreview] = useState<string | null>(null)

  // Manejar dropzone
  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    setDropError(undefined)

    if (rejectedFiles && rejectedFiles.length > 0) {
      rejectedFiles.forEach((file: any) => {
        file.errors.forEach((err: any) => {
          if (err.code === 'file-invalid-type') setDropError('Solamente podés subir archivos JPG, JPEG o PNG');
          else setDropError('Error desconocido');
        });
      });
      return;
    }

    const file = acceptedFiles[0]
    if (!file) return

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setDropError(`El tamaño máximo de la imagen es de ${MAX_IMAGE_SIZE_MB}MB`)
      return
    }

    const reader = new FileReader()
    reader.onloadend = () => setPreview(reader.result as string)
    reader.readAsDataURL(file)
  }, [])

  const {
    getRootProps,
    getInputProps,
    open: openFiles,
    fileRejections,
    isDragActive
  } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'image/jpg': []
    },
    maxFiles: 1
  })

  const uploadFile = useCallback(async () => {
    setLoading(true)
    try {
      const imagenBase64 = preview.includes(',') ? preview.split(',')[1] : preview

      await axiosInstanceMain.post(`admin/${tenant.path}/locales/${codLocal}/imagenes`, {
        imagenBase64,
        titulo: titulo,
        sacadaPorNosotros: true
      })
      SnackBarUtils.success('Imagen publicada correctamente')
      updateGaleria()
    } catch (e) {
      console.log(e)
    } finally {
      setLoading(false)
      handleClose()
    }
  }, [preview, titulo, tenant, codLocal])

  const handleClose = () => {
    setPreview(null)
    setTitulo(null)
    setOpen(false)
  }

  return (
    <>
      <Dialog
        open={open}
        PaperProps={{
          style: { overflowX: 'hidden', overflowY: 'auto', padding: '0.4rem' }
        }}
        onClose={handleClose}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '20px' }}>
          <InputLabel
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'right',
              width: '100%'
            }}
          >
            <Typography variant="subtitle2">
              {titulo?.length || 0}/{MAX_TITULO_LENGTH}
            </Typography>
          </InputLabel>
          <TextField
            label="Título"
            disabled={loading}
            variant="outlined"
            sx={{ width: '100%' }}
            onChange={(e) => setTitulo(e.target.value)}
            inputProps={{ maxLength: MAX_TITULO_LENGTH }}
          />
          {/* Dropzone */}
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed #aaa',
              borderRadius: 2,
              p: preview ? 0 : 3, // si hay preview, sacamos padding
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: isDragActive ? 'action.hover' : 'transparent',
              display: 'inline-block' // para que se ajuste al contenido
            }}
          >
            <input {...getInputProps()} />

            {!preview ? (
              <>
                {!isMobile && (
                  <Typography>
                    {isDragActive ? 'Soltá la foto aquí...' : 'Arrastrá una foto o hacé click para seleccionar'}
                  </Typography>
                )}

                <Button component="label" variant="outlined" sx={{ mt: 2 }} onClick={() => openFiles()}>
                  Agregar Foto
                </Button>
              </>
            ) : (
              <>
                <Box sx={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                  <img
                    src={preview}
                    alt="preview"
                    style={{
                      maxWidth: '100%',
                      display: 'block',
                      borderRadius: '8px'
                    }}
                  />

                  {/* Botón para eliminar la imagen */}
                  <IconButton
                    onClick={() => setPreview(null)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                    }}
                  >
                    <DeleteOutline />
                  </IconButton>
                </Box>
              </>
            )}
          </Box>
        </Box>
        <DialogActions>
          <IconButton
            edge="end"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: '0',
              right: '0',
              paddingRight: '16px'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogActions>
        <Box
          sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', margin: '0px 20px 20px 20px' }}
        >
          <Button disabled={loading} sx={{ width: '48%' }} variant="contained" color="error" onClick={handleClose}>
            Cancelar
          </Button>
          <Button disabled={loading} sx={{ width: '48%' }} variant="contained" color="success" onClick={uploadFile}>
            Publicar
          </Button>
        </Box>
      </Dialog>
      {dropError !== undefined && (
        <Dialog open={dropError !== undefined} onClose={() => setDropError(undefined)} maxWidth="lg">
          <DialogTitle>Errores en la subida de imagenes</DialogTitle>
          <DialogContent>{dropError}</DialogContent>
          <DialogActions>
            <Button onClick={() => setDropError(undefined)}>Cerrar</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}

export default UploadImg
